import { Button, Flex, Group, Modal, Paper, Stack, Switch, Title } from "@mantine/core";
import {
  IConsequenceArray,
  IEvaluationCategoryApi,
  IGridConsequenceWithId,
  IIssueBoardApi,
  IMatrixConfig,
  IUpdateConsequence,
} from "@mcapps/shared";
import { ICapitalLetter } from "@schemas/otherAttributesSchema";
import { ColDef, ColDefField } from "ag-grid-community";
import { IconCircle, IconCircleCheck } from "@tabler/icons-react";
import React from "react";
import {
  FailedAlert,
  FoldableSectionDeprecated,
  HSpace,
  McInputNew,
  MCLoader,
  McTitle,
  TitleWithValue,
  VSpace,
} from "../../../../../../components";
import ViewEditTable from "../../../../../../components/ViewEditTable/ViewEditTable";
import {
  usePostProjectByProjectIdIssueboardRisikoEvaluationcategoryAddMutation,
  usePostProjectByProjectIdIssueboardRisikoEvaluationcategoryUpdateMutation,
} from "../../../../../../../openapi";
import ColorMatrixSettings from "./ColorMatrixSettings";

const EvaluationCategories: React.FC<{ issueBoard: IIssueBoardApi }> = ({ issueBoard }) => {
  const [addEvaluationCategory, { error: addEvaluationCategoryError, isLoading: isLoadingAdd }] =
    usePostProjectByProjectIdIssueboardRisikoEvaluationcategoryAddMutation();
  const [updateEvaluationCategory, { error: updateEvaluationCategoryError, isLoading: isLoadingUpdate }] =
    usePostProjectByProjectIdIssueboardRisikoEvaluationcategoryUpdateMutation();

  const evaluationCategoryTypeEnum = {
    RISK: "RISK",
    OPPORTUNITY: "OPPORTUNITY",
  } as const;

  const colSettings: ColDef<IGridConsequenceWithId>[] = [
    {
      field: "id",
      headerName: "ID",
      width: 70,
    },
    {
      field: "name",
      headerName: "Navn",
      editable: true,
    },
    {
      field: "description",
      headerName: "Beskrivelse",
      editable: true,
    },
    {
      field: "matrixValue",
      headerName: "Matriseverdi",
      editable: true,
      type: "numericColumn",
    },
    /*{
      field: "order",
      headerName: "Order",
      editable: false,
    },*/
    {
      field: "isActive",
      headerName: "Aktiv",
      editable: true,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: ["Ja", "Nei"],
      },
    },
  ];

  const template: IGridConsequenceWithId = {
    id: "",
    name: "",
    description: "",
    matrixValue: 1,
    isActive: "Ja",
    order: 0,
  };

  const saveCategory = async (
    prop: ColDefField<IEvaluationCategoryApi>,
    newValue: string | boolean | IConsequenceArray | IMatrixConfig,
    id: string,
  ) => {
    const send = {
      projectId: issueBoard.project,
      body: {
        issueBoardId: issueBoard._id,
        prop,
        newValue: newValue as any,
        id,
      },
    };
    await updateEvaluationCategory(send);
  };

  const getData = (cat: IEvaluationCategoryApi): IGridConsequenceWithId[] => {
    const d: IGridConsequenceWithId[] = Object.entries(cat.consequences).map(([key, value]) => ({
      ...value,
      isActive: value.isActive === true ? ("Ja" as "Ja" | "Nei") : "Nei",
      id: key,
    }));

    return d;
  };

  const _saveConsequences = (consequences: IConsequenceArray, categoryLetter: ICapitalLetter) => {
    const consequencesWithOrder = consequences.map((consequence, index) => ({
      ...consequence,
      order: index,
    }));
    saveCategory("consequences", consequencesWithOrder, categoryLetter);
  };

  const isError = addEvaluationCategoryError || updateEvaluationCategoryError;

  const [addEvalModalOpen, setAddEvalModalOpen] = React.useState<boolean>(false);

  const _add = (name: string) => {
    addEvaluationCategory({
      projectId: issueBoard.project,
      body: {
        issueBoardId: issueBoard._id,
        name: name,
      },
    });
    setAddEvalModalOpen(false);
  };
  return (
    <Paper withBorder p="sm" radius="sm" bg="#fdfdff">
      {addEvalModalOpen && <AddEvalModal onCreate={_add} close={() => setAddEvalModalOpen(false)} />}
      <Stack gap="xs">
        <Title order={4} fw={400}>
          <Flex style={{ alignItems: "center" }}>
            <span style={{ marginRight: "0.5rem" }}>Evalueringskategorier</span>
            <MCLoader loading={isLoadingAdd === true || isLoadingUpdate === true} size={5} />
          </Flex>
        </Title>
        {isError && <FailedAlert />}
        {Object.entries(issueBoard.risikoSection?.evaluationCategoriesConfig)?.map(([key, cat]) => {
          const _cat = cat as IEvaluationCategoryApi;
          return (
            <Paper withBorder p="sm" radius="sm" bg="#fdfdff" mb="0.5rem" key={key}>
              <FoldableSectionDeprecated folded={_cat.isActive === false}>
                <FoldableSectionDeprecated.Header>
                  <Flex style={{ alignItems: "center", marginBottom: "0.5rem" }}>
                    <h3 style={{ margin: 0, marginRight: "0.5rem" }}>#{key}</h3>
                    <HSpace />
                    <Switch
                      size="sm"
                      onChange={() => saveCategory("isActive", !_cat.isActive, key)}
                      checked={_cat.isActive}
                    />
                    <HSpace />
                    <Title order={3} mb={0}>
                      {cat.isActive === false && <s>{_cat.name}</s>}
                      {cat.isActive === true && _cat.name}
                    </Title>
                  </Flex>
                </FoldableSectionDeprecated.Header>
                <FoldableSectionDeprecated.Body>
                  <Flex direction="column" id={"__points_table_" + key} w="100%">
                    <VSpace />
                    <TitleWithValue
                      allowEmpty={false}
                      title="Tittel"
                      value={_cat.name}
                      editable
                      saveCb={(oldValue, newValue) => saveCategory("name", newValue, key)}
                    />
                    <McTitle title="Risko eller muliget">
                      <div>
                        <Group gap="2px" wrap="nowrap">
                          <Button
                            style={{ borderRadius: "6px 0 0 6px" }}
                            size="sm"
                            px="xs"
                            color={_cat.type === evaluationCategoryTypeEnum.RISK ? "mc-primary" : "warmgray.6"}
                            variant={_cat.type === evaluationCategoryTypeEnum.RISK ? "filled" : "light"}
                            onClick={() => saveCategory("type", evaluationCategoryTypeEnum.RISK, key)}
                            rightSection={
                              _cat.type === evaluationCategoryTypeEnum.RISK ? (
                                <IconCircleCheck size={18} />
                              ) : (
                                <IconCircle size={18} />
                              )
                            }
                          >
                            Risiko
                          </Button>
                          <Button
                            style={{ borderRadius: "0 6px 6px 0" }}
                            size="sm"
                            px="xs"
                            color={_cat.type === evaluationCategoryTypeEnum.OPPORTUNITY ? "mc-primary" : "warmgray.6"}
                            variant={_cat.type === evaluationCategoryTypeEnum.OPPORTUNITY ? "filled" : "light"}
                            onClick={() => saveCategory("type", evaluationCategoryTypeEnum.OPPORTUNITY, key)}
                            rightSection={
                              _cat.type === evaluationCategoryTypeEnum.OPPORTUNITY ? (
                                <IconCircleCheck size={18} />
                              ) : (
                                <IconCircle size={18} />
                              )
                            }
                          >
                            Mulighet
                          </Button>
                        </Group>
                      </div>
                    </McTitle>
                    <VSpace />
                    <McTitle title="Konsekvens">
                      <ViewEditTable
                        columns={colSettings}
                        data={getData(_cat)}
                        newItemTemplateObject={template}
                        onSave={(data) => {
                          const newData = data.map((item) => ({
                            ...item,
                            isActive: item.isActive === "Ja",
                          }));
                          _saveConsequences(newData, key as ICapitalLetter);
                        }}
                        canEdit={issueBoard.risikoSection?.isActive === true}
                        canRemovePredicate={(item) => item.id === ""}
                        orderProp="order"
                      />
                    </McTitle>
                    <ColorMatrixSettings issueBoard={issueBoard} evaluationCategoryKey={key} />
                  </Flex>
                </FoldableSectionDeprecated.Body>
              </FoldableSectionDeprecated>
            </Paper>
          );
        })}

        <Flex>
          <Button color="green" onClick={() => setAddEvalModalOpen(true)}>
            <i className="fa fa-plus fa-fw" />
            Ny
          </Button>
        </Flex>
      </Stack>
    </Paper>
  );
};

export default EvaluationCategories;

const AddEvalModal: React.FC<{ close: () => void; onCreate: (name: string) => void }> = ({ close, onCreate }) => {
  const [name, setName] = React.useState("");

  return (
    <Modal opened onClose={close} title="Ny kategori" yOffset={120}>
      <div>
        <McInputNew
          autoFocus
          placeholder={`Skriv inn navn på kategori (kost, fremdrift etc)`}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <VSpace />
      </div>
      <Group justify="flex-end" gap="xs" mt="lg">
        <Button disabled={name === ""} onClick={() => onCreate(name)}>
          OPPRETT
        </Button>
        <Button variant="default" onClick={close}>
          Avbryt
        </Button>
      </Group>
    </Modal>
  );
};
